/**
 * Per-store identity — sourced from store.config.ts (Rule #19, Rule #13).
 *
 * `STORE.ID` is the per-store slug: the SAME value `scripts/generate-configs.cjs`
 * substitutes into the wrangler / route-seed `{{SLUG}}` placeholders at build
 * time. Using it here means the observability `store_id` tag and the session
 * cookie names can never drift from the deployed resource names.
 */

import { storeConfig } from '../../store.config'

export const STORE = {
  ID: storeConfig.slug,
  /** Brand display name — pulled from store.config.ts.brand.name. Used
   *  in customer-facing artefacts (PDF quote header, etc.). */
  NAME: storeConfig.brand.name
} as const

/**
 * Tenant identity (Kiran 2026-06-07).
 *
 * MASTERORG_ID — id of the seeded Uyyamadilu master organisation
 * (migration 0104). Resolves apex traffic + serves as the default for
 * back-fills and not-yet-assigned references. Must stay in lockstep
 * with the migration's literal id; changing one requires changing the
 * other.
 *
 * MASTERORG_SUBDOMAIN — the subdomain slug carried on the master org's
 * row; the tenant resolver returns this org when the host is the apex.
 */
export const TENANT = {
  MASTERORG_ID: 'a1a1a1a1-a1a1-4a1a-aa1a-a1a1a1a1a1a1',
  MASTERORG_SUBDOMAIN: 'uyyamadilu'
} as const

/**
 * Cloudflare infrastructure identifiers (Phase 4, Kiran 2026-06-08).
 *
 * These are NOT secrets — they're public resource identifiers that
 * match the values in `wrangler.web.toml`. The Build click backend
 * uses them to attach `<slug>.uyyamadilu.com` as a custom domain on
 * the web worker via the Cloudflare API. The token itself lives in
 * CONFIG_KV under `secret:cloudflare_api_token` (KVKEYS.CLOUDFLAREAPITOKEN).
 *
 * If you fork this framework onto a different Cloudflare account or
 * zone, these values change in lockstep with the wrangler config.
 */
export const CLOUDFLARE = {
  ACCOUNT_ID: '0129d5c7cb1d1a6d58575a0bba045286',
  ZONE_ID: 'c19e58daa8d6c87deaed464cbf160a7d',
  /** Worker script name to attach tenant domains to (matches the
   *  `name = "web"` in wrangler.web.toml). */
  WEB_WORKER_NAME: 'web',
  /** Apex domain — all tenant slugs become subdomains of this. */
  APEX_DOMAIN: 'uyyamadilu.com'
} as const
